import { useAuthFrontendApis } from '@propelauth/frontend-apis-react';
import { useAuthInfo } from '@propelauth/react';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';

interface SignupFormProps {
  email: string;
  onSuccess?: (user: any) => void;
  onSwitchToLogin?: () => void;
}

export function SignupForm({ email, onSuccess, onSwitchToLogin }: SignupFormProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
    firstName?: string;
    lastName?: string;
    general?: string;
  }>({});

  const { signup } = useAuthFrontendApis();
  const { refreshAuthInfo, user } = useAuthInfo();

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!firstName) {
      newErrors.firstName = 'First name is required';
    }

    if (!lastName) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await signup({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });

      await response.handle({
        success: async data => {
          console.log('Signup success data:', data);
          await refreshAuthInfo();

          // Pass the signup response data to the auth controller
          onSuccess?.({
            email: email,
            loginState: data?.login_state,
          });
        },
        badRequest: error => {
          console.log('BadRequest error:', error);
          setErrors({
            password: error.user_facing_errors?.password,
            firstName: error.user_facing_errors?.['first_name'],
            lastName: error.user_facing_errors?.['last_name'],
            general: error.user_facing_error || 'Failed to create account. Please try again.',
          });
        },
        unexpectedOrUnhandled: error => {
          console.log('Unexpected error:', error);
          const errorMessage =
            error?.user_facing_error || 'An unexpected error occurred. Please try again.';
          setErrors({ general: errorMessage });
        },
      });
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
        className="space-y-6"
      >
        {errors.general && (
          <Alert variant="destructive">
            <AlertDescription>{errors.general}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSignup} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email-signup" className="text-sm font-medium text-neutral-950">
              Email
            </Label>
            <Input
              id="email-signup"
              type="email"
              value={email}
              className="h-11 border-neutral-200 text-neutral-950"
              disabled
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-sm font-medium text-neutral-950">
                First Name
              </Label>
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className="h-11 border-neutral-200 focus:ring-black focus:border-black text-neutral-950"
                required
              />
              {errors.firstName && <p className="text-sm text-red-600">{errors.firstName}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-sm font-medium text-neutral-950">
                Last Name
              </Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className="h-11 border-neutral-200 focus:ring-black focus:border-black text-neutral-950"
                required
              />
              {errors.lastName && <p className="text-sm text-red-600">{errors.lastName}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-neutral-950">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="pr-10 h-11 border-neutral-200 focus:ring-black focus:border-black text-neutral-950"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute text-neutral-400 transition-colors -translate-y-1/2 right-3 top-1/2 hover:text-neutral-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-red-600">{errors.password}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium text-neutral-950">
              Confirm Password
            </Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="pr-10 h-11 border-neutral-200 focus:ring-black focus:border-black text-neutral-950"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute text-neutral-400 transition-colors -translate-y-1/2 right-3 top-1/2 hover:text-neutral-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-red-600">{errors.confirmPassword}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full bg-black h-11 hover:bg-gray-800 font-medium"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner size={16} className="mr-2" color="white" />
                Creating account...
              </>
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        <div className="text-center">
          <p className="text-sm text-neutral-600">
            Already have an account?{' '}
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="font-medium text-black transition-colors hover:text-gray-700 hover:underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
